/*
 * The administrator's first step (VISUAL.md §7.16, D-10 §2.4): one button that makes the channel
 * everybody joins. It creates `#allgemein` with the topic "The whole team", tries to make it the
 * instance's default channel — the setting every account is added to at its next connect — and then
 * opens it, where the empty-channel block says what to do next.
 *
 * The default-channel write (`PUT /api/admin/settings/chat.default_channel`, `{value, ifVersion}`)
 * is a step-up action: a session whose password is older than fifteen minutes is refused. On this
 * screen the session is minutes old, so the tier normally passes; when it does not, the password
 * field stands *inside* this form (`useStepUpField`) and never in a dialog over the first-run screen
 * (D-10 D11). The password is spent on the **settings read that comes first**, so a wrong one costs
 * nothing at all. The write itself is still allowed to fail: a key the server does not register or a
 * version somebody else moved (409) leaves the channel standing, and a toast says the one thing that
 * did not happen, "Saved without Everyone joins", with where to set it later.
 *
 * The write is core's `adminApi.writeDefaultChannel`, the one the conversation's details use too.
 * The read is `readSettingVersion`: `adminApi.readDefaultChannel` answers a key the server does not
 * register as "no default channel yet", and this door must tell that apart — a key that is not there
 * is not written at all. `ifVersion` is the version `GET /api/admin/settings` answers for the key —
 * 0 for a key nobody has written yet.
 */

import { adminApi, DEFAULT_CHANNEL_KEY } from "../../../core/admin/api.ts"
import { useStepUpField } from "../../../core/auth/index.ts"
import { translate } from "../../../core/i18n/index.ts"
import { useToast } from "../../../core/shell/index.ts"
import { useAsyncAction } from "../../../lib/useAsyncAction.ts"
import { Alert, Button } from "../../../ui/index.ts"
import { createConversation, readSettingVersion } from "../conversation/api.ts"
import { goTo, whenConversationArrives } from "../conversation/opening.ts"
import { t } from "../i18n/t.ts"
import { conversationPath } from "../navigation.ts"

/** The address of the channel everybody joins — the product's word for it since the first day. */
const DEFAULT_SLUG = "allgemein"

export function CreateFirstChannel({ onDone }: { onDone: () => void }) {
	const stepUp = useStepUpField()
	const toast = useToast()
	const create = useAsyncAction(async () => {
		// The password is spent on the settings read, before anything is created, so a password the
		// server refuses costs nothing: the form's own alert says so, no channel exists, and pressing
		// again works. Spent on the write instead it left a channel standing, a toast about a setting
		// and no door back — the first row of the checklist was ticked by the channel (N4-F REQUESTS 3).
		// A read that fails for any other reason stops the door too: an instance that cannot answer
		// what its settings are is not one to create the team's first channel in.
		const version = await stepUp.submit(() => readSettingVersion(DEFAULT_CHANNEL_KEY))
		const created = await createConversation({
			kind: "channel",
			slug: DEFAULT_SLUG,
			name: DEFAULT_SLUG,
			topic: t("chat.home.firstChannel.topic"),
			memberIds: [],
		})
		const id = created.conversation.id
		let everyone = false
		if (version !== undefined) {
			try {
				await adminApi.writeDefaultChannel(id, version)
				everyone = true
			} catch {
				// The channel exists; only the setting did not take. Said below, never retried by itself:
				// a 409 means somebody else moved the key while this screen was open.
			}
		}
		if (!everyone) toast({ kind: "info", key: "chat.home.firstChannel.withoutEveryone" })
		await whenConversationArrives(id)
		onDone()
		goTo(conversationPath(created.conversation.slug ?? DEFAULT_SLUG))
	})

	return (
		<form
			className="tb-setup__form"
			onSubmit={(event) => {
				event.preventDefault()
				void create.run()
			}}
		>
			{stepUp.field}
			{create.error === null ? null : <Alert kind="danger">{translate(create.error)}</Alert>}
			<Button
				type="submit"
				variant="primary"
				size="sm"
				busy={create.busy}
				disabled={create.busy}
				label={t("chat.home.firstChannel.create", { channel: `#${DEFAULT_SLUG}` })}
			/>
		</form>
	)
}
